import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef } from 'react'
import { Toaster } from 'sonner'
import { Button } from '@/components/ui/button'
import { AppShell } from '@/components/shell/AppShell'
import { EtainablSpinner } from '@/components/etainabl-spinner'
import { ensureSessionStart, trackPageView } from '@/lib/analytics'
import { isCallbackPath } from '@/lib/returnTo'

// Exported (not just used locally) so the chrome-escape behavior — AppShell
// present for ordinary routes, structurally absent for /tracker/present — is
// directly testable without standing up a full router tree.
export function RootGuard() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0()
  const location = useLocation()
  // StrictMode runs effects twice; a double loginWithRedirect creates two
  // auth transactions, the returning ?state matches neither, the code
  // exchange fails, and the guard loops through Auth0 forever. The ref
  // survives the double-invoke so login fires exactly once.
  const loginStarted = useRef(false)

  // While on /callback the provider is busy consuming ?code&state — starting
  // a new login there discards the in-flight transaction.
  const onCallback = isCallbackPath(location.pathname)

  // Presentation mode is a full-screen deck shared on a client's screen — the
  // sidebar, nav and view-as switcher must be structurally absent, not merely
  // hidden by CSS, so there is nothing left in the DOM for a client to
  // glimpse. Same pathname-check mechanism as onCallback above, but
  // case-insensitive: TanStack's router matches routes case-insensitively by
  // default, so `/TRACKER/PRESENT` still resolves this route and must still
  // escape the shell — a case-sensitive `===`/`startsWith` here would render
  // the full chrome around the deck for that casing.
  const onPresentation = location.pathname.toLowerCase() === '/tracker/present'

  useEffect(() => {
    if (isLoading || isAuthenticated || error || onCallback) return
    if (loginStarted.current) return
    loginStarted.current = true
    loginWithRedirect({ appState: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` } })
  }, [isLoading, isAuthenticated, error, onCallback, loginWithRedirect])

  useEffect(() => {
    if (!isAuthenticated) return
    ensureSessionStart()
    trackPageView(location.pathname)
  }, [isAuthenticated, location.pathname])

  // Auth errors render a dead-end with a manual retry — never auto-loop.
  if (error && !isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">Sign-in failed: {error.message}</p>
        <Button size="sm" onClick={() => { loginStarted.current = true; loginWithRedirect() }}>
          Try again
        </Button>
      </div>
    )
  }

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <EtainablSpinner size="lg" />
      </div>
    )
  }
  if (onPresentation) {
    // No <Toaster/> here — deliberately, not an oversight. When both branches
    // render a same-shaped fragment (routed content + Toaster), React
    // reconciles by position and keeps the SAME Toaster instance mounted
    // across the transition into presentation mode, so a toast already
    // on-screen (a tracker-field edit's toast.success/error, 4s default)
    // rides along onto the shared deck instead of being unmounted with the
    // rest of the chrome. Dropping this child changes the fragment's shape,
    // which forces React to unmount it here.
    return <Outlet />
  }

  return (
    <>
      <AppShell><Outlet /></AppShell>
      <Toaster richColors position="bottom-right" closeButton />
    </>
  )
}

export const Route = createRootRoute({ component: RootGuard })
